import axios from 'axios';

const configuredBackendUrl = process.env.REACT_APP_BACKEND_URL?.replace(/\/$/, '');
const API_BASE = configuredBackendUrl ? `${configuredBackendUrl}/api` : '/api';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Token management
const getToken = () => localStorage.getItem('access_token');
const setToken = (token) => localStorage.setItem('access_token', token);
const removeToken = () => localStorage.removeItem('access_token');

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      removeToken();
      window.dispatchEvent(new Event('gpj:auth-expired'));
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  getRegistrationConsents: () => api.get('/auth/registration-consents'),
  register: (userData) => api.post('/auth/register', userData),
  login: (credentials) => api.post('/auth/login', credentials),
  getCurrentUser: () => api.get('/auth/me'),
  logout: () => {
    removeToken();
    return Promise.resolve();
  }
};

export const accessAPI = {
  getState: () => api.get('/access'),
  selectPath: (path) => api.post('/access/select', { path }),
  getEntitlements: () => api.get('/access/entitlements')
};

export const membershipAPI = {
  getStatus: () => api.get('/payments/membership'),
  createCheckout: () => api.post('/payments/membership/checkout'),
  createPortal: () => api.post('/payments/membership/portal')
};

export const referralAPI = {
  getCurrent: () => api.get('/referrals/current'),
  authorizeUpload: (referralId, contentType, sizeBytes) => api.post(
    `/referrals/${encodeURIComponent(referralId)}/upload-authorization`,
    { content_type: contentType, size_bytes: sizeBytes }
  ),
  submit: (referralId) => api.post(
    `/referrals/${encodeURIComponent(referralId)}/submit`
  )
};

export const uploadEvidenceDirectly = async (authorization, file) => {
  const body = new FormData();
  Object.entries(authorization.form_fields || {}).forEach(([name, value]) => {
    body.append(name, value);
  });
  body.append('file', file);
  const response = await fetch(authorization.upload_url, {
    method: authorization.upload_method,
    body
  });
  if (!response.ok) {
    throw new Error('Evidence storage rejected the upload.');
  }
};

export const referralAdminAPI = {
  list: () => api.get('/admin/referrals'),
  get: (referralId) => api.get(
    `/admin/referrals/${encodeURIComponent(referralId)}`
  ),
  createReviewAccess: (referralId) => api.post(
    `/admin/referrals/${encodeURIComponent(referralId)}/review-access`
  ),
  decide: (referralId, decision, reasonCode) => api.post(
    `/admin/referrals/${encodeURIComponent(referralId)}/decision`,
    { decision, reason_code: reasonCode }
  )
};

export const workflowAPI = {
  list: () => api.get('/workflows'),
  getDefinition: (workflowId, version = null) => api.get(
    `/workflows/${encodeURIComponent(workflowId)}`,
    { params: version ? { version } : {} }
  ),
  start: (workflowId, version = null) => api.post(
    `/workflows/${encodeURIComponent(workflowId)}/instances`,
    null,
    { params: version ? { version } : {} }
  ),
  getInstance: (instanceId) => api.get(
    `/workflow-instances/${encodeURIComponent(instanceId)}`
  ),
  submitStep: (instanceId, stepId, submission) => api.post(
    `/workflow-instances/${encodeURIComponent(instanceId)}/steps/${encodeURIComponent(stepId)}`,
    submission
  )
};

export const writingReviewAPI = {
  review: (text, locale = 'en-CA') => api.post('/ai/text-review', { text, locale })
};

export const formsCatalogueAPI = {
  list: () => api.get('/forms/catalogue'),
  get: (formId) => api.get(`/forms/catalogue/${encodeURIComponent(formId)}`),
  getFieldGuide: (formId, catalogueVersion, guideVersion) => api.get(
    `/forms/catalogue/${encodeURIComponent(formId)}/field-guide`,
    {
      params: {
        catalogue_version: catalogueVersion,
        guide_version: guideVersion
      }
    }
  ),
  startWorkflow: (formId) => api.post(
    `/forms/catalogue/${encodeURIComponent(formId)}/workflow`
  )
};

export const appealsAPI = {
  getModule: () => api.get('/appeals/module'),
  evaluate: (rulesetVersion, answers) => api.post('/appeals/path', {
    ruleset_version: rulesetVersion,
    answers
  })
};

export const directoryAPI = {
  search: ({ q = null, entryType = null, category = null, region = null, limit = 20 } = {}) => {
    const params = {};
    if (q) params.q = q;
    if (entryType) params.entry_type = entryType;
    if (category) params.category = category;
    if (region) params.region = region;
    params.limit = limit;
    return api.get('/directory', { params });
  },
  get: (entryId) => api.get(`/directory/${encodeURIComponent(entryId)}`)
};

// Cases API
export const casesAPI = {
  create: (caseData) => api.post('/cases', caseData),
  getAll: () => api.get('/cases'),
  getById: (id) => api.get(`/cases/${id}`),
  update: (id, data) => api.put(`/cases/${id}`, data),
  delete: (id) => api.delete(`/cases/${id}`)
};

// Calendar API
export const calendarAPI = {
  createEvent: (eventData) => api.post('/calendar/events', eventData),
  getEvents: ({ startAt = null, endAt = null, limit = 50 } = {}) => {
    const params = { limit };
    if (startAt) params.start_at = startAt;
    if (endAt) params.end_at = endAt;
    return api.get('/calendar/events', { params });
  },
  getById: (id) => api.get(`/calendar/events/${encodeURIComponent(id)}`),
  updateEvent: (id, data) => api.put(`/calendar/events/${id}`, data),
  deleteEvent: (id) => api.delete(`/calendar/events/${id}`)
};

// Forms API
export const formsAPI = {
  create: (formData) => api.post('/forms', formData),
  getAll: () => api.get('/forms'),
  getById: (id) => api.get(`/forms/${id}`),
  update: (id, data) => api.put(`/forms/${id}`, data),
  submit: (id) => api.post(`/forms/${id}/submit`)
};

// Resources API
export const resourcesAPI = {
  getAll: (resourceType = null, category = null, search = null, limit = 20) => {
    const params = new URLSearchParams();
    if (resourceType) params.append('resource_type', resourceType);
    if (category) params.append('category', category);
    if (search) params.append('search', search);
    params.append('limit', limit.toString());
    return api.get(`/resources?${params}`);
  },
  getById: (id) => api.get(`/resources/${id}`)
};

// Contacts API
export const contactsAPI = {
  getAll: (contactType = null, region = null, emergencyOnly = false) => {
    const params = new URLSearchParams();
    if (contactType) params.append('contact_type', contactType);
    if (region) params.append('region', region);
    if (emergencyOnly) params.append('emergency_only', 'true');
    return api.get(`/contacts?${params}`);
  }
};

// Notifications API
export const notificationsAPI = {
  getAll: (unreadOnly = false) => {
    const params = new URLSearchParams();
    if (unreadOnly) params.append('unread_only', 'true');
    return api.get(`/notifications?${params}`);
  },
  markAsRead: (id) => api.put(`/notifications/${id}/read`)
};

// Token utilities
export { getToken, setToken, removeToken };

export default api;
